import React, { useState, useRef, useEffect } from 'react';

interface EntityTypeSelectorProps {
  availableTypes: string[];
  onSelect: (entityType: string) => void;
  onCancel: () => void;
  position: { top: number; left: number };
  initialValue?: string;
}

const EntityTypeSelector: React.FC<EntityTypeSelectorProps> = ({
  availableTypes,
  onSelect,
  onCancel,
  position,
  initialValue
}) => {
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [filteredTypes, setFilteredTypes] = useState<string[]>(availableTypes);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus input when component mounts
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus({ preventScroll: true } as any);
        // If there's an initial value (editing mode), select all text for easy replacement
        if (initialValue) {
          try {
            el.select();
          } catch {}
        } else {
          // Otherwise just position cursor at end
          const len = el.value.length;
          try {
            el.setSelectionRange(len, len);
          } catch {}
        }
      }
    });
  }, [initialValue]);

  useEffect(() => {
    // Filter types based on input
    if (inputValue.trim() === '') {
      setFilteredTypes(availableTypes);
    } else {
      const filtered = availableTypes.filter(type =>
        type.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredTypes(filtered);
    }
    // Start with no selection so user must navigate or we can show "create new"
    setSelectedIndex(0);
  }, [inputValue, availableTypes]);

  // Check if input exactly matches a filtered type
  const hasExactMatch = filteredTypes.some(
    type => type.toLowerCase() === inputValue.trim().toLowerCase()
  );
  
  // Show "Create new" option if there's input and no exact match
  const showCreateNew = inputValue.trim() !== '' && !hasExactMatch;

  useEffect(() => {
    // Handle clicks outside
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  const handleSelect = (type: string) => {
    onSelect(type);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        if (showCreateNew && selectedIndex === 0) {
          // First item is "Create new", so create new label
          handleSelect(inputValue.trim());
        } else if (filteredTypes.length > 0) {
          // Select from filtered types (adjust index if "Create new" is shown)
          const typeIndex = showCreateNew ? selectedIndex - 1 : selectedIndex;
          if (typeIndex >= 0 && typeIndex < filteredTypes.length) {
            handleSelect(filteredTypes[typeIndex]);
          }
        } else {
          // No matches, create new
          handleSelect(inputValue.trim());
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const maxIndex = filteredTypes.length + (showCreateNew ? 1 : 0) - 1;
      setSelectedIndex(prev => Math.min(prev + 1, maxIndex));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed bg-white border border-gray-300 rounded-lg shadow-2xl"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        minWidth: '250px',
        maxWidth: '350px',
        zIndex: 9999
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="p-2 border-b border-gray-200">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type or select entity type..."
          autoFocus
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="max-h-60 overflow-y-auto">
        <ul className="py-1">
          {/* Show "Create new" option as first item if input doesn't exactly match */}
          {showCreateNew && (
            <li
              className={`px-3 py-2 text-sm cursor-pointer border-b border-gray-200 ${
                selectedIndex === 0
                  ? 'bg-green-100 text-green-900 font-medium'
                  : 'text-green-700 hover:bg-green-50'
              }`}
              onClick={() => handleSelect(inputValue.trim())}
              onMouseEnter={() => setSelectedIndex(0)}
            >
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create "{inputValue.trim()}"
              </div>
            </li>
          )}
          
          {/* Show filtered types */}
          {filteredTypes.length > 0 ? (
            <>
              {filteredTypes.map((type, index) => {
                const displayIndex = showCreateNew ? index + 1 : index;
                return (
                  <li
                    key={type}
                    className={`px-3 py-2 text-sm cursor-pointer ${
                      displayIndex === selectedIndex
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-900 hover:bg-gray-100'
                    }`}
                    onClick={() => handleSelect(type)}
                    onMouseEnter={() => setSelectedIndex(displayIndex)}
                  >
                    {type}
                  </li>
                );
              })}
            </>
          ) : !showCreateNew ? (
            <li className="px-3 py-2 text-sm text-gray-500">
              No matching types found
            </li>
          ) : null}
        </ul>
      </div>

      <div className="p-2 border-t border-gray-200 text-xs text-gray-500">
        <span>↑↓ Navigate • Enter to select • Esc to cancel</span>
        {showCreateNew && selectedIndex === 0 && (
          <div className="mt-1 text-green-700 font-medium">
            ⏎ Create new "{inputValue.trim()}"
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityTypeSelector;

